import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import {
  Row,
  Button,
  H1,
  IconWrapper,
  Icon,
  Copy,
  Box,
  Optional,
  Wrapper,
  useMediaQueries,
} from '@honda-canada/design-system-react';
import { useHistory } from 'react-router-dom';
import ProductLineTabs from './ProductLineTabs';
import DropdownSelectors from './DropDownSelectors';
import themeStyles from './styles/SelectProducts.styles';
import ProductDetails from '../ProductDetails';
import PaymentOverview from '../PaymentOverview';
import Accordions from '../Accordions/index';
import PriceAndFinanceDataProvider from '../PriceAndFinanceContext';
import {
  useCurrentProductLine,
  useSelectedOptions,
  useExteriorColor,
  useFinance,
} from '../SelectProductsContext/SelectProductsProvider';
import useDropdownOptions from '../../hooks/useDropdownOptions';
import getDropdownOptionsData, { optionsKeys } from '../../utils/getDropdownOptionsData';
import {
  MC_PRODUCT_LINE_NAME,
  PRIMARY_ID_KEY,
  productLineFromVehicleType,
  DEFAULT_FINANCE_OPTION,
  DEFAULT_FINANCE_TERM,
} from '../../utils/constants';
import useLayoutService from '../../hooks/useLayoutService';
import { getDefaultExteriorColorKey } from '../ProductDetails/utils';
import Disclaimer from '../Disclaimer';
import pushGtmDropdownFilterEvent from './utils';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';

const StyledRow = themeStyles.apply(Row, 'StyledRow');
const StyledWrapper = themeStyles.apply(Box, 'StyledWrapper');
const PrimaryButton = themeStyles.apply(Button, 'PrimaryButton');
const SelectVehicle = themeStyles.apply(Box, 'SelectVehicle');
const SelectVehicleWrapper = themeStyles.apply(Box, 'SelectVehicleWrapper');
const SelectProductButton = themeStyles.apply(Copy, 'SelectProductButton');
const SelectedProductName = themeStyles.apply(Copy, 'SelectedProductName');
const Arrow = themeStyles.apply(Icon, 'Arrow');
const SelectVehicleContainer = themeStyles.apply(Wrapper, 'SelectVehicleContainer');

const PSP_DROPDOWN_SCHEMA = [
  {
    key: 'category',
    name: 'category',
    label: 'Sub-apps.PriceAndFinance.categoryLabel',
    placeholder: 'Sub-apps.PriceAndFinance.selectCategoryPlaceholder',
    width: ['auto', 'auto', '220px'],
  },
  {
    key: 'year',
    name: 'year',
    label: 'Sub-apps.PriceAndFinance.yearLabel',
    placeholder: 'Sub-apps.PriceAndFinance.selectYearPlaceholder',
    width: ['auto', 'auto', '160px'],
  },
  {
    key: 'model',
    name: 'model',
    label: 'Sub-apps.PriceAndFinance.modelLabel',
    placeholder: 'Sub-apps.PriceAndFinance.selectModelPlaceholder',
    width: ['auto', 'auto', '300px'],
  },
  {
    key: 'trim',
    name: 'trim',
    label: 'Sub-apps.PriceAndFinance.trimLabel',
    placeholder: 'Sub-apps.PriceAndFinance.selectTrimPlaceholder',
    width: ['auto', 'auto', '220px'],
  },
];

const PSP_PRODUCTLINE_NAMES = {
  MC: 'motorcycle',
  ATV: 'atv',
  SXS: 'side-by-side',
};

const SelectProducts = ({ dropdownList, availableProductLines }) => {
  const { isMobile, isSmallDesktop } = useMediaQueries();
  const [currentProductLine, setCurrentProductLine] = useCurrentProductLine();
  const { resetSelectedOptions } = useSelectedOptions();
  const { dropdownOptions } = useDropdownOptions();
  const [, setFinance] = useFinance();
  const [shouldRender, setShouldRender] = useState(false);

  const history = useHistory();
  const params = new URLSearchParams(history?.location?.search);
  const paramsObj = Object.fromEntries(params);

  useEffect(() => {
    if (!paramsObj?.vehicleType) {
      setCurrentProductLine(MC_PRODUCT_LINE_NAME);
    } else {
      setCurrentProductLine(productLineFromVehicleType.get(paramsObj?.vehicleType));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [categoryData, yearData, modelData, transmissionData] = getDropdownOptionsData(dropdownOptions, [
    optionsKeys.category,
    optionsKeys.year,
    optionsKeys.model,
    optionsKeys.transmission,
  ]);

  const getProductLineName = productLine => {
    if (PSP_PRODUCTLINE_NAMES?.hasOwnProperty.call(PSP_PRODUCTLINE_NAMES, productLine)) {
      return PSP_PRODUCTLINE_NAMES[productLine];
    }
    return null;
  };

  const [selectedExteriorColor, setSelectedExteriorColor] = useExteriorColor();
  // cb function for setting default color upon successful API request
  const onSuccess = data => {
    const defaultColor = getDefaultExteriorColorKey(data) || '';
    setSelectedExteriorColor(defaultColor);
    setShouldRender(true);
  };
  // pre-fetch product details data
  const { layout, isFetching, hasError } = useLayoutService(transmissionData?.id, true, onSuccess);
  const modelName = modelData?.text;
  const modelYear = yearData?.value;
  const modelTransmission = transmissionData?.value;
  const modelCategory = categoryData?.value;
  const selectedTrimId = transmissionData?.[PRIMARY_ID_KEY];

  // isViewPricingButtonActive is a derived state from selectedTrimId
  const isViewPricingButtonActive = !!selectedTrimId;

  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(true);

  const selectVehicleHandler = () => {
    if (isViewPricingButtonActive) {
      pushGtmDropdownFilterEvent(
        getProductLineName(currentProductLine),
        modelCategory,
        `${modelYear}: ${modelTransmission}: ${modelName}`,
      );
      setIsExpanded(prevState => !prevState);
      // Reset the finance, term, downpayment on model change
      setFinance({ options: DEFAULT_FINANCE_OPTION, term: DEFAULT_FINANCE_TERM, downPayment: 0 });
    }
  };

  return (
    <StyledRow>
      <SelectVehicleWrapper
        data-is-opened={isExpanded}
        isMobile={isMobile || isSmallDesktop}
        data-testid="SelectVehicleWrapper"
        isExpanded={isExpanded}
      >
        <SelectVehicleContainer>
          <SelectVehicle isProductSelected={!isExpanded} onClick={selectVehicleHandler}>
            <SelectProductButton
              data-testid="select-vehicle-button"
              disabled={!isViewPricingButtonActive}
              isViewPricingButtonActive={isViewPricingButtonActive}
              as="button"
            >
              {t('Sub-apps.PriceAndFinance.selectModelPlaceholder')}
            </SelectProductButton>
            <IconWrapper>
              <Arrow
                width="12px"
                height="7px"
                name="arrowDown"
                rotated={isExpanded}
                color={isViewPricingButtonActive ? 'black' : 'grey'}
              />
            </IconWrapper>
          </SelectVehicle>
          <Optional when={!isExpanded && modelYear && modelName}>
            <SelectedProductName>{`${modelYear} ${modelName}`}</SelectedProductName>
          </Optional>
        </SelectVehicleContainer>
      </SelectVehicleWrapper>
      <Wrapper>
        <Optional when={isExpanded}>
          <StyledWrapper data-testid="cy-price-and-finance-overview">
            <H1 data-testid="price-and-finance-title">{t('Sub-apps.PriceAndFinance.priceAndFinanceHeading')}</H1>
            <ProductLineTabs
              currentProductLine={currentProductLine}
              setCurrentProductLine={setCurrentProductLine}
              resetSelectedOptions={resetSelectedOptions}
              availableProductLines={availableProductLines}
            />
            <DropdownSelectors
              // TODO: add switching for multiple JSS site support
              dropdownSchema={PSP_DROPDOWN_SCHEMA}
              productCatalogDataTree={dropdownList}
              // add key prop to force rerender when product line change
              key={currentProductLine}
              setIsExpanded={setIsExpanded}
            />
            <PrimaryButton
              data-testid="cy-view-price-and-finance-button"
              styling="primary"
              onClick={selectVehicleHandler}
              disabled={!isViewPricingButtonActive}
            >
              {t('Sub-apps.PriceAndFinance.viewPriceAndFinanceButton')}
            </PrimaryButton>
          </StyledWrapper>
        </Optional>
        <Optional when={isFetching}>
          <LoadingSpinner fullHeight />
        </Optional>
        <Optional when={shouldRender && !isExpanded && selectedTrimId && !isFetching}>
          <PriceAndFinanceDataProvider>
            <ProductDetails
              modelYear={modelYear}
              modelName={modelName}
              isFetching={isFetching}
              hasError={hasError}
              layout={layout}
              selectedExteriorColor={selectedExteriorColor}
              setSelectedExteriorColor={setSelectedExteriorColor}
            />
            <PaymentOverview />
            <Accordions selectedTrimId={selectedTrimId} />
            <Disclaimer />
          </PriceAndFinanceDataProvider>
        </Optional>
      </Wrapper>
    </StyledRow>
  );
};

SelectProducts.propTypes = {
  dropdownList: PropTypes.shape({}),
  availableProductLines: PropTypes.arrayOf(PropTypes.string),
};
export default SelectProducts;
